import Skeleton from "./Skeleton";

// 0018: shape variants composed from the base <Skeleton>. Each mirrors a real layout so the
// skeleton->content swap causes no layout shift. Spec C reuses these as-is (no new infra).

// N lines of text. Optional shorter last line for a natural paragraph look.
export const SkeletonText = ({ lines = 3, lastLineWidth = "60%", className = "" }) => (
  <div className={`skeletonGroup ${className}`.trim()}>
    {Array.from({ length: Math.max(1, lines) }, (_, index) => (
      <Skeleton key={index} variant="text" width={index === lines - 1 && lines > 1 ? lastLineWidth : "100%"} />
    ))}
  </div>
);

// Simple stacked full-width rows (e.g. a plain list).
export const SkeletonList = ({ rows = 6, className = "" }) => (
  <div className={`skeletonList ${className}`.trim()}>
    {Array.from({ length: Math.max(1, rows) }, (_, index) => (
      <Skeleton key={index} variant="rounded" />
    ))}
  </div>
);

// Header + body rows of equal-flex cells. Mirrors the manage-style flex tables.
export const SkeletonTable = ({ rows = 8, cols = 4, showHeader = true, className = "" }) => {
  const cells = (n) => Array.from({ length: Math.max(1, n) }, (_, index) => <Skeleton key={index} variant="text" />);
  return (
    <div className={`skeletonTable ${className}`.trim()}>
      {showHeader && <div className="skeletonTable__row skeletonTable__row--header">{cells(cols)}</div>}
      {Array.from({ length: Math.max(1, rows) }, (_, index) => (
        <div key={index} className="skeletonTable__row">
          {cells(cols)}
        </div>
      ))}
    </div>
  );
};

// Grid of product-card placeholders (image block + two text lines). Mirrors .productListContainer.
export const SkeletonCardGrid = ({ count = 8, className = "" }) => (
  <div className={`skeletonCardGrid ${className}`.trim()}>
    {Array.from({ length: Math.max(1, count) }, (_, index) => (
      <div key={index} className="skeletonCard">
        <Skeleton variant="rect" className="skeletonCard__image" />
        <Skeleton variant="text" width="80%" />
        <Skeleton variant="text" width="50%" />
      </div>
    ))}
  </div>
);

// N labeled field placeholders (label bar + input bar).
export const SkeletonForm = ({ fields = 4, className = "" }) => (
  <div className={`skeletonForm ${className}`.trim()}>
    {Array.from({ length: Math.max(1, fields) }, (_, index) => (
      <div key={index} className="skeletonForm__field">
        <Skeleton variant="text" className="skeletonForm__label" />
        <Skeleton variant="rounded" className="skeletonForm__input" />
      </div>
    ))}
  </div>
);

// A select/multi-select input placeholder. multi adds a row of chip placeholders.
export const SkeletonSelect = ({ multi = false, chips = 4, className = "" }) => (
  <div className={`skeletonSelect ${className}`.trim()}>
    <Skeleton variant="text" className="skeletonSelect__label" />
    <Skeleton variant="rounded" className="skeletonSelect__input" />
    {multi && (
      <div className="skeletonSelect__chips">
        <Skeleton variant="rounded" count={Math.max(1, chips)} />
      </div>
    )}
  </div>
);

// Detail panel: image block + info column (title, meta lines, action). Mirrors .itemHeader.
export const SkeletonDetail = ({ lines = 4, className = "" }) => (
  <div className={`skeletonDetail ${className}`.trim()}>
    <Skeleton variant="rect" className="skeletonDetail__image" />
    <div className="skeletonDetail__info">
      <Skeleton variant="text" className="skeletonDetail__title" />
      {Array.from({ length: Math.max(1, lines) }, (_, index) => (
        <Skeleton key={index} variant="text" width={`${85 - index * 10}%`} />
      ))}
      <Skeleton variant="rounded" className="skeletonDetail__button" />
    </div>
  </div>
);
